import React, { useEffect, useMemo, useState } from 'react';

import { DndContext, DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import type { RJSFSchema } from '@rjsf/utils';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, FormControl, IconButton, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';

import ArrayFieldItem from '~/components/Common/Forms/fields/ArrayFieldItem';

import { useUpdateOffer } from '~/hooks/apis/offers/useGetOffer';

import getErrorMessage from '~/utils/getErrorMessage';

interface Props {
  campaigns?: Array<string>;
  holidayTypes?: Array<string>;
  locations?: Array<string>;
  offerId: string;
  offerSchema?: RJSFSchema;
  offerType?: string;
}

interface DraftTags {
  campaigns: Array<string>;
  holidayTypes: Array<string>;
  locations: Array<string>;
}

interface ArrayFieldSchema {
  items?: {
    enum?: unknown;
  };
}

export default function TagsAndMarketing({
  campaigns,
  holidayTypes,
  locations,
  offerId,
  offerSchema,
  offerType,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const updateOfferMutation = useUpdateOffer(offerId);
  const [draftTags, setDraftTags] = useState<DraftTags>(() => buildDraftTags({ campaigns, holidayTypes, locations }));
  const tagOptions = useMemo(() => getTagOptions(offerSchema), [offerSchema]);

  useEffect(() => {
    setDraftTags(buildDraftTags({ campaigns, holidayTypes, locations }));
  }, [campaigns, holidayTypes, locations]);

  async function handleSave() {
    try {
      await updateOfferMutation.mutateAsync({
        ...(offerType ? { type: offerType } : {}),
        campaigns: draftTags.campaigns,
        holiday_types: draftTags.holidayTypes,
        locations: draftTags.locations,
      });
      enqueueSnackbar('Tags & Marketing saved', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to save Tags & Marketing'), { variant: 'error' });
    }
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Tags & Marketing</Typography>
          <Typography variant="body1" color="text.secondary">
            Review and adjust the offer tags shown across merchandising and discovery.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={updateOfferMutation.isPending}
          sx={{ flexShrink: 0 }}
        >
          {updateOfferMutation.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </Stack>

      {!offerSchema && <Alert severity="info">Loading tag options.</Alert>}

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Stack spacing={2}>
          {renderTagArrayField({
            label: 'Location Tags',
            options: tagOptions.locations,
            value: draftTags.locations,
            onChange: (value) => setDraftTags((currentTags) => ({ ...currentTags, locations: value })),
          })}
          {renderTagArrayField({
            label: 'Holiday Type Tags',
            options: tagOptions.holidayTypes,
            value: draftTags.holidayTypes,
            onChange: (value) => setDraftTags((currentTags) => ({ ...currentTags, holidayTypes: value })),
          })}
          {renderTagArrayField({
            label: 'Campaign Tags',
            options: tagOptions.campaigns,
            value: draftTags.campaigns,
            onChange: (value) => setDraftTags((currentTags) => ({ ...currentTags, campaigns: value })),
          })}
        </Stack>
      </Box>
    </Box>
  );
}

function renderTagArrayField({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<string>;
  value: Array<string>;
  onChange: (value: Array<string>) => void;
}) {
  const sortableItems: Array<UniqueIdentifier> = value.map((item, index) => `${item}-${index}`);

  function updateItem(index: number, nextValue: string) {
    const nextArray = [...value];
    nextArray.splice(index, 1, nextValue);
    onChange(nextArray);
  }

  function removeItem(index: number) {
    const nextArray = [...value];
    nextArray.splice(index, 1);
    onChange(nextArray);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onChange(arrayMove(value, sortableItems.indexOf(active.id), sortableItems.indexOf(over.id)));
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="h6">{label}</Typography>
        <DndContext onDragEnd={handleDragEnd}>
          <SortableContext items={sortableItems}>
            {value.map((item, index) => (
              <FormControl key={`${item}-${index}`} fullWidth>
                <ArrayFieldItem itemKey={item} index={index}>
                  <>
                    <FormControl fullWidth>
                      <Select
                        fullWidth
                        aria-label={label}
                        value={item}
                        onChange={(event) => updateItem(index, event.target.value)}
                      >
                        {options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton color="error" onClick={() => removeItem(index)}>
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </>
                </ArrayFieldItem>
              </FormControl>
            ))}
          </SortableContext>
        </DndContext>
        {!value.length && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
            <Typography variant="body2">Empty</Typography>
          </Box>
        )}
        <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            color="primary"
            size="large"
            onClick={() => onChange([...value, options[0] ?? ''])}
            disabled={!options.length}
          >
            <AddIcon fontSize="large" />
          </IconButton>
        </Box>
      </Stack>
    </Paper>
  );
}

function buildDraftTags({
  campaigns,
  holidayTypes,
  locations,
}: Pick<Props, 'campaigns' | 'holidayTypes' | 'locations'>): DraftTags {
  return {
    campaigns: campaigns ?? [],
    holidayTypes: holidayTypes ?? [],
    locations: locations ?? [],
  };
}

function getTagOptions(schema: RJSFSchema | undefined) {
  return {
    campaigns: getEnumValues(schema, 'campaigns'),
    holidayTypes: getEnumValues(schema, 'holiday_types'),
    locations: getEnumValues(schema, 'locations'),
  };
}

function getEnumValues(schema: RJSFSchema | undefined, field: string): Array<string> {
  const fieldSchema = schema?.properties?.[field];
  let enumValues: unknown;

  if (isArrayFieldSchema(fieldSchema)) {
    enumValues = fieldSchema.items?.enum;
  }

  return Array.isArray(enumValues) ? enumValues.filter((value): value is string => typeof value === 'string') : [];
}

function isArrayFieldSchema(fieldSchema: unknown): fieldSchema is ArrayFieldSchema {
  return !!fieldSchema && typeof fieldSchema === 'object' && !Array.isArray(fieldSchema);
}
